import { ActionType, UpdateLastVisitedNotificationsAction } from '@/actions/actiontypes';
import { Config } from '@/bootstrap/config';
import { stateConfig, stateNotifications, stateSplash } from '@/selectors/selectors';
import { Notification } from '@/state';
import { SplashState } from '@/state/SplashState';
import { useDispatch, useSelector } from '@/state/store';
import { SVVAlert } from '@komponentkassen/svv-alert';
import { SVVButton } from '@komponentkassen/svv-button';
import { SVVModal } from '@komponentkassen/svv-modal';
import { useEffect, useState } from 'react';

interface AlertEntryProps {
    date: Date;
    message: HTMLElement | string;
    onClick?: () => void;
    link?: string;
}

export interface Feilmelding {
    id: string;
    arsak: 'HENDELSE' | 'MERKNAD' | 'DRIFTSSTANS' | 'VEDLIKEHOLD' | 'INGEN_PROBLEMER';
    pavirkedeSystemer: { systemID: string }[];
    datoLaget: string;
    tittel: string;
    innhold: string;
}

const AlertEntry = ({ date, message, onClick, link }: AlertEntryProps) => {
    const simpleDate = date.toLocaleDateString('nb-no', {
        day: '2-digit',
        month: 'short',
    });
    const simpleTime = date.toLocaleTimeString('nb-no', { hour: '2-digit', minute: '2-digit' });
    return (
        <li className="c-notification__alert__entry" onClick={onClick}>
            <p dangerouslySetInnerHTML={{ __html: message }}></p>
            <p className="c-notification__alert__entry__date" title={date.toISOString()}>
                {`${simpleDate} - ${simpleTime}`}
            </p>
            {link && (
                <a
                    className="c-notification__alert__link"
                    href={link}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Se hendelse (NVDB Status)
                </a>
            )}
        </li>
    );
};

export const fetchFeilmeldinger = async (config: Config) => {
    const response: Feilmelding[] = await fetch(`${config.status.api}/api/v1/feilmeldinger/aktiv`)
        .then(res => res.json())
        .then(json => json.feilmeldinger)
        .catch(e => {
            console.error('Klarte ikke å hente feilmeldinger', e);
            return { feilmeldinger: [] };
        });
    return response.filter(f => f.pavirkedeSystemer.flatMap(s => s.systemID).includes(config.status.vegkart));
};

interface NotificationDialogProps {
    handleWindowChange: (window: SplashState) => void;
}

export const NotificationDialog = ({ handleWindowChange }: NotificationDialogProps) => {
    const modalState = useSelector(stateSplash);
    const config = useSelector(stateConfig);
    const dispatch = useDispatch();

    const [hasIssue, setHasIssue] = useState(false);
    const [statusAlerts, setStatusAlerts] = useState<Feilmelding[]>([]);

    const notificationsState: Notification[] = useSelector(stateNotifications).toSorted(
        (a, b) => b.time.getTime() - a.time.getTime()
    );

    const handleClose = () => {
        handleWindowChange(SplashState.NONE);
    };

    const getNewestAlertTime = (feilmeldinger: Feilmelding[]): Date => {
        const lastNotification = new Date(notificationsState[0]?.time).getTime()
            ? new Date(notificationsState[0]?.time)
            : new Date(0);
        const lastStatusAlert = new Date(feilmeldinger[0]?.datoLaget).getTime()
            ? new Date(feilmeldinger[0]?.datoLaget)
            : new Date(0);
        return new Date(lastNotification).getTime() - new Date(lastStatusAlert).getTime()
            ? lastStatusAlert
            : lastNotification;
    };

    useEffect(() => {
        fetchFeilmeldinger(config).then(res => {
            if (res.some(f => f.arsak !== 'INGEN_PROBLEMER')) {
                setHasIssue(true);
                setStatusAlerts(
                    res
                        .filter(f => f.arsak !== 'INGEN_PROBLEMER')
                        .sort((a, b) => new Date(b.datoLaget).getTime() - new Date(a.datoLaget).getTime())
                );
            } else {
                setHasIssue(false);
            }
            const action: UpdateLastVisitedNotificationsAction = {
                type: ActionType.LAST_VISITED_NOTIFICATIONS_UPDATED,
                date: getNewestAlertTime(
                    res.sort((a, b) => new Date(b.datoLaget).getTime() - new Date(a.datoLaget).getTime())
                ),
            };
            dispatch(action);
        });
    }, [config, dispatch]);

    const alertType = hasIssue ? 'warning' : 'success';
    const alertMessage = hasIssue ? 'Vegkart har en hendelse.' : 'Vegkart fungerer som normalt.';
    return (
        <SVVModal
            className="c-notification"
            show={modalState === SplashState.NOTIFICATION}
            onClose={handleClose}
            title="Varsler"
            acceptButton={<SVVButton onClick={handleClose}>Lukk vindu</SVVButton>}
        >
            <div className="c-notification__content">
                <SVVAlert type={alertType} size="lg" isCenter>
                    <div className="c-notification__alert__content">
                        <p>{alertMessage}</p>
                        <p>
                            <a href={config.status.url} target="_blank" rel="noreferrer noopener">
                                Se hendelser (NVDB Status)
                            </a>
                        </p>
                    </div>
                </SVVAlert>
                <ul className="c-notification__alert">
                    {statusAlerts.length > 0 &&
                        statusAlerts.map(issue => (
                            <AlertEntry
                                key="status"
                                date={new Date(issue.datoLaget)}
                                message={issue.tittel}
                                link={config.status.url + '/#/hendelser?id=' + issue.id}
                            />
                        ))}
                    {notificationsState.map((n, i) => (
                        <AlertEntry
                            key={'alert' + i.toString()}
                            date={n.time}
                            message={n.message}
                            onClick={n.callback}
                        />
                    ))}
                    <li className="c-notification__alert__end">
                        {notificationsState.length || statusAlerts.length
                            ? 'Ingen eldre varsler'
                            : 'Ingen varsler'}
                    </li>
                </ul>
            </div>
        </SVVModal>
    );
};

NotificationDialog.displayName = 'NotificationDialog';
export default NotificationDialog;
